import React, { useState } from 'react';

const API_URL = '';

export default function ComingSoonModal({ product, onClose }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleNotify = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/preorder`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim() || 'Interested Customer',
          email: email.trim(),
          message: 'Notify me when available',
          productId: product?.id || '',
          productName: product?.name || ''
        })
      });
      if (!res.ok) throw new Error('Failed to submit');
      setSubmitted(true);
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div style={styles.header}>
          <h2 style={styles.title}>Coming Soon</h2>
          <button onClick={onClose} style={styles.closeBtn}>×</button>
        </div>

        {/* Product Info */}
        {product && (
          <div style={styles.productInfo}>
            {(product.image || product.cover) && (
              <img
                src={product.image || product.cover}
                alt={product.name}
                style={styles.productImage}
                onError={e => e.target.style.display = 'none'}
              />
            )}
            <div>
              <p style={styles.productName}>{product.name}</p>
              <p style={styles.productPrice}>${(product.price / 100).toFixed(2)}</p>
            </div>
          </div>
        )}

        {/* Secured Badge */}
        <div style={styles.securedBadge}>
          <span>🔒</span>
          <span>Secured — Your information is protected</span>
        </div>

        {/* Message */}
        <div style={styles.body}>
          {!submitted ? (
            <>
              <p style={styles.message}>
                Thank you for your interest! This product is currently in the preorder phase.
                You will be notified by email once the preorder goal is met and the product
                becomes available for purchase.
              </p>
              <p style={styles.subMessage}>
                Leave your email below and we'll reach out as soon as it's ready.
              </p>

              <form onSubmit={handleNotify} style={styles.form}>
                {error && <div style={styles.error}>{error}</div>}
                <input
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="Your Name (Optional)"
                  style={styles.input}
                />
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="Your Email Address *"
                  style={styles.input}
                  required
                />
                <div style={styles.buttons}>
                  <button
                    type="button"
                    onClick={onClose}
                    style={styles.cancelBtn}
                    disabled={submitting}
                  >
                    Maybe Later
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    style={{
                      ...styles.confirmBtn,
                      opacity: submitting ? 0.7 : 1,
                      cursor: submitting ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {submitting ? 'Submitting...' : 'Notify Me'}
                  </button>
                </div>
              </form>
            </>
          ) : (
            <div style={styles.successBox}>
              <div style={styles.successIcon}>✅</div>
              <p style={styles.successTitle}>You're on the list!</p>
              <p style={styles.successText}>
                We'll notify you at <strong>{email}</strong> as soon as{' '}
                <strong>{product?.name}</strong> is available for purchase.
              </p>
              <button onClick={onClose} style={styles.confirmBtn}>
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    backdropFilter: 'blur(4px)'
  },
  modal: {
    background: 'linear-gradient(135deg, #1a1a1a 0%, #0a0a0a 100%)',
    borderRadius: '12px',
    maxWidth: '460px',
    width: '90%',
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 8px 32px rgba(206,0,33,0.3), 0 0 0 1px rgba(206,0,33,0.2)',
    border: '1px solid rgba(206,0,33,0.3)'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px',
    borderBottom: '1px solid rgba(206,0,33,0.3)'
  },
  title: {
    margin: 0,
    fontSize: '22px',
    color: '#ce0021',
    fontWeight: 'bold'
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '32px',
    cursor: 'pointer',
    color: '#ce0021',
    padding: 0,
    width: '32px',
    height: '32px'
  },
  productInfo: {
    display: 'flex',
    gap: '1rem',
    padding: '15px 20px',
    borderBottom: '1px solid rgba(206,0,33,0.2)',
    alignItems: 'flex-start'
  },
  productImage: {
    width: '90px',
    height: '90px',
    objectFit: 'cover',
    borderRadius: '8px',
    border: '1px solid rgba(206,0,33,0.3)',
    flexShrink: 0
  },
  productName: {
    margin: '0 0 4px 0',
    fontSize: '16px',
    fontWeight: '600',
    color: '#fff'
  },
  productPrice: {
    margin: 0,
    fontSize: '18px',
    color: '#ce0021',
    fontWeight: 'bold'
  },
  securedBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '8px 20px',
    background: 'rgba(0,200,83,0.08)',
    borderBottom: '1px solid rgba(0,200,83,0.2)',
    color: '#00c853',
    fontSize: '0.82rem',
    fontWeight: '600'
  },
  body: {
    padding: '20px'
  },
  message: {
    color: '#fff',
    fontSize: '0.95rem',
    lineHeight: '1.6',
    marginBottom: '0.75rem'
  },
  subMessage: {
    color: '#aaa',
    fontSize: '0.85rem',
    marginBottom: '1.2rem'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  error: {
    backgroundColor: 'rgba(206,0,33,0.2)',
    color: '#ff6b7a',
    padding: '10px',
    borderRadius: '6px',
    fontSize: '13px',
    border: '1px solid rgba(206,0,33,0.4)'
  },
  input: {
    width: '100%',
    padding: '12px',
    fontSize: '15px',
    border: '1px solid rgba(206,0,33,0.3)',
    borderRadius: '6px',
    boxSizing: 'border-box',
    backgroundColor: 'rgba(255,255,255,0.05)',
    color: '#fff',
    outline: 'none'
  },
  buttons: {
    display: 'flex',
    gap: '10px',
    justifyContent: 'flex-end',
    marginTop: '4px'
  },
  confirmBtn: {
    padding: '12px 24px',
    background: 'linear-gradient(135deg,#ce0021 0%,#a00019 100%)',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '15px',
    fontWeight: 'bold',
    cursor: 'pointer',
    boxShadow: '0 4px 15px rgba(206,0,33,0.4)',
    transition: 'all 0.3s'
  },
  cancelBtn: {
    padding: '12px 24px',
    backgroundColor: 'rgba(255,255,255,0.1)',
    color: '#fff',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: '6px',
    fontSize: '15px',
    cursor: 'pointer',
    transition: 'all 0.3s'
  },
  successBox: {
    textAlign: 'center',
    padding: '1rem 0'
  },
  successIcon: {
    fontSize: '3rem',
    marginBottom: '1rem'
  },
  successTitle: {
    color: '#00c853',
    fontSize: '1.4rem',
    fontWeight: 'bold',
    margin: '0 0 0.5rem 0'
  },
  successText: {
    color: '#ccc',
    fontSize: '0.95rem',
    lineHeight: '1.6',
    marginBottom: '1.5rem'
  }
};
